package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.*
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import kotlinx.coroutines.launch
import kotlin.math.max

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun CreateOrderScreen(
    supplier: Supplier,
    onBack: () -> Unit,
    onOrderCreated: () -> Unit
) {
    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }

    var items by remember { mutableStateOf<List<Item>>(emptyList()) }
    var itemNames by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    var isLoading by remember { mutableStateOf(true) }

    val defaultDays = remember(supplier.name) {
        when {
            supplier.name.contains("Abevco", ignoreCase = true) -> "11"
            supplier.name.contains("Lesta Packaging", ignoreCase = true) -> "11"
            supplier.name.contains("Chiltern Veg", ignoreCase = true) -> "3"
            supplier.name.contains("Chiltern", ignoreCase = true) -> "2"
            supplier.name.contains("Chicken", ignoreCase = true) -> "2"
            supplier.name.contains("Halal Editions", ignoreCase = true) -> "4"
            supplier.name.contains("bob labels", ignoreCase = true) -> "28"
            else -> "7"
        }
    }
    
    var daysCalculated by remember { mutableStateOf(defaultDays) }
    val itemQuantities = remember { mutableStateMapOf<String, String>() }
    val itemStock = remember { mutableStateMapOf<String, Double>() }
    val itemUsage = remember { mutableStateMapOf<String, Double>() }
    val lastCountDetails = remember { mutableStateMapOf<String, StockCountItem>() }
    
    var showExportDialog by remember { mutableStateOf<List<OrderItem>?>(null) }
    var createdOrderId by remember { mutableStateOf<String?>(null) }

    fun calculateSuggestions(itemList: List<Item>, stockMap: Map<String, Double>, days: String) {
        val daysValue = days.toDoubleOrNull() ?: 7.0
        val isBOC = supplier.name.contains("BOC", ignoreCase = true)
        
        val thresholdMultiplier = 1.0 + (supplier.thresholdOrderPercentage ?: 0).toDouble() / 100.0
        
        itemList.forEach { item ->
            val currentStock = stockMap[item.id] ?: 0.0
            itemStock[item.id] = currentStock

            if (isBOC) {
                itemQuantities[item.id] = "3"
                itemUsage[item.id] = 0.0
                return@forEach
            }

            // usage = (weeklyUsage / 7 * days) * (1 + threshold/100)
            val baseUsageForPeriod = (item.weeklyUsageRate / 7.0 * daysValue)
            val usageForPeriod = baseUsageForPeriod * thresholdMultiplier
            itemUsage[item.id] = usageForPeriod

            // suggested = usage - currentStock
            val neededAmount = usageForPeriod - currentStock
            val rawSuggested = max(0.0, neededAmount)

            val stepSize = getOrderStepSize(item, supplier.name)
            val finalSuggestion = when {
                stepSize == 5 -> {
                    // Round up to next 5
                    kotlin.math.ceil(rawSuggested / 5.0) * 5.0
                }
                else -> {
                    // Round up to next whole number
                    kotlin.math.ceil(rawSuggested)
                }
            }
            
            // Format for display: remove .0 as we want whole numbers
            val displayValue = finalSuggestion.toInt().toString()
            
            itemQuantities[item.id] = displayValue
        }
    }

    fun loadData() {
        scope.launch {
            isLoading = true
            val itemsResult = repository.getItems(supplier.id)
            if (itemsResult.isSuccess) {
                val itemList = itemsResult.getOrThrow()
                items = itemList
                itemNames = itemList.associate { it.id to it.name }
                
                // Fetch latest stock count
                val countsResult = repository.getStockCounts(supplier.id)
                val latestCount = countsResult.getOrNull()?.firstOrNull()
                
                val stockMap = mutableMapOf<String, Double>()
                if (latestCount != null) {
                    repository.getStockCountDetails(latestCount.id).onSuccess { details ->
                        details.forEach { detail ->
                            stockMap[detail.itemId] = detail.countedRealAmount
                            lastCountDetails[detail.itemId] = detail
                        }
                    }
                }
                
                calculateSuggestions(itemList, stockMap, daysCalculated)
            }
            isLoading = false
        }
    }

    LaunchedEffect(supplier.id) {
        loadData()
    }

    fun generateOrder() {
        val orderItems = items.mapNotNull { item ->
            val qtyRaw = itemQuantities[item.id]?.toDoubleOrNull() ?: 0.0
            val qty = kotlin.math.ceil(qtyRaw)
            if (qty > 0) {
                OrderItem(
                    itemId = item.id,
                    qtySuggested = itemUsage[item.id] ?: 0.0,
                    qtyOrdered = qty
                )
            } else null
        }

        if (orderItems.isEmpty()) {
            scope.launch { snackbarHostState.showSnackbar("Please add at least one item") }
            return
        }

        scope.launch {
            val existingId = createdOrderId
            if (existingId == null) {
                val request = CreateOrderRequest(
                    supplierId = supplier.id,
                    daysCalculated = daysCalculated.toIntOrNull() ?: 7,
                    status = "Pending",
                    items = orderItems
                )
                repository.createOrder(request).fold(
                    onSuccess = { resp ->
                        if (resp.error == null) {
                            if (resp.id != null) {
                                createdOrderId = resp.id
                            }
                            showExportDialog = orderItems
                        } else {
                            snackbarHostState.showSnackbar("Error: ${resp.error}")
                        }
                    },
                    onFailure = {
                        snackbarHostState.showSnackbar("Network error: ${it.message}")
                    }
                )
            } else {
                val request = EditOrderRequest(
                    id = existingId,
                    status = "Pending",
                    items = orderItems
                )
                repository.editOrder(request).fold(
                    onSuccess = { resp ->
                        if (resp.error == null) {
                            showExportDialog = orderItems
                        } else {
                            snackbarHostState.showSnackbar("Error: ${resp.error}")
                        }
                    },
                    onFailure = {
                        snackbarHostState.showSnackbar("Network error: ${it.message}")
                    }
                )
            }
        }
    }

    Box(modifier = Modifier.fillMaxSize()) {
        Scaffold(
            snackbarHost = { SnackbarHost(snackbarHostState) },
            topBar = {
                TopAppBar(
                    title = { Text("New Order: ${supplier.name}") },
                    navigationIcon = {
                        IconButton(onClick = onBack) {
                            BackArrowIcon()
                        }
                    },
                    actions = {
                        if (!isLoading) {
                            OverflowActionsMenu(
                                actions = listOf(OverflowAction("Generate") { generateOrder() })
                            )
                        }
                    }
                )
            }
        ) { padding ->
            Box(modifier = Modifier.padding(padding).fillMaxSize()) {
                if (!isLoading && items.isEmpty()) {
                    Text("No items found for this supplier.", modifier = Modifier.align(Alignment.Center))
                } else if (!isLoading) {
                    Column(modifier = Modifier.fillMaxSize()) {
                        // Days selector header
                        Surface(
                            color = MaterialTheme.colorScheme.surfaceVariant,
                            modifier = Modifier.fillMaxWidth()
                        ) {
                            Row(
                                modifier = Modifier.padding(16.dp).fillMaxWidth(),
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.SpaceBetween
                            ) {
                                Text("Order Cover Days:", fontWeight = FontWeight.Bold)
                                NumberStepper(
                                    value = daysCalculated,
                                    onValueChange = {
                                        daysCalculated = it
                                        calculateSuggestions(items, itemStock, it)
                                    },
                                    min = 1.0
                                )
                            }
                        }

                        // Table Header
                        Row(
                            modifier = Modifier.fillMaxWidth().background(MaterialTheme.colorScheme.primary).padding(horizontal = 8.dp, vertical = 8.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Text("Item", modifier = Modifier.weight(2.2f), color = MaterialTheme.colorScheme.onPrimary, fontWeight = FontWeight.Bold)
                            Text("Usage", modifier = Modifier.weight(0.7f), color = MaterialTheme.colorScheme.onPrimary, fontWeight = FontWeight.Bold, textAlign = TextAlign.Center)
                            Text("Qty", modifier = Modifier.weight(1.3f), color = MaterialTheme.colorScheme.onPrimary, fontWeight = FontWeight.Bold, textAlign = TextAlign.Center)
                        }

                        LazyColumn(modifier = Modifier.weight(1f)) {
                            items(items) { item ->
                                val usage = itemUsage[item.id] ?: 0.0
                                val qtyText = itemQuantities[item.id] ?: "0"

                                Row(
                                    modifier = Modifier.fillMaxWidth().padding(horizontal = 8.dp, vertical = 6.dp),
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    Column(modifier = Modifier.weight(2.2f).padding(end = 6.dp)) {
                                        Text(item.name, fontWeight = FontWeight.Bold, maxLines = 2, overflow = TextOverflow.Ellipsis)
                                        Text("${item.packDescription} (${item.unitMeasureType})", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.onSurfaceVariant, maxLines = 1, overflow = TextOverflow.Ellipsis)
                                        val lastDetail = lastCountDetails[item.id]
                                        val stockDisplay = formatStockAmount(item, lastDetail?.countedRealAmount ?: itemStock[item.id] ?: 0.0)
                                        Text("Stock: $stockDisplay", style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.primary)
                                    }

                                    Text(
                                        text = formatWhole(usage),
                                        modifier = Modifier.weight(0.7f),
                                        textAlign = TextAlign.Center,
                                        style = MaterialTheme.typography.bodySmall
                                    )

                                    NumberStepper(
                                        value = qtyText,
                                        onValueChange = { itemQuantities[item.id] = it },
                                        modifier = Modifier.weight(1.3f),
                                        step = getOrderStepSize(item, supplier.name).toDouble()
                                    )
                                }
                                HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.1f))
                            }
                        }
                    }
                }
            }
        }

        if (isLoading) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(MaterialTheme.colorScheme.background)
                    .clickable(enabled = false) { },
                contentAlignment = Alignment.Center
            ) {
                BrandLoadingSpinner(Modifier.fillMaxSize())
            }
        }

        showExportDialog?.let { orderItems ->
            OrderExportDialog(
                supplier = supplier,
                items = items,
                itemNames = itemNames,
                itemStocks = itemStock,
                orderItems = orderItems,
                daysCalculated = daysCalculated,
                onDismiss = {
                    showExportDialog = null
                    onOrderCreated()
                }
            )
        }
    }
}

fun getOrderStepSize(item: Item, supplierName: String = ""): Int {
    val name = item.name.lowercase().trim()
    val supplier = supplierName.lowercase().trim()

    val isBlackAngus = name.contains("black angus beef", ignoreCase = true)
    val isChickenSupplier = supplier.contains("chicken", ignoreCase = true)

    return if (isBlackAngus || isChickenSupplier) 5 else 1
}
